import { AlertCircle, Check, Loader2, RefreshCw } from 'lucide-react';

import type { AutosaveState } from '../../hooks/useProjectAutosave';
import { Button } from '@/shared/components/ui/button';
import { messages } from '@/shared/constants/messages';

interface AutosaveIndicatorProps {
  state: AutosaveState;
  onReload: () => void;
  /**
   * Modo ajuste (decisão 48): a promessa é a mesma, o substantivo é outro.
   * Um projeto devolvido não voltou a ser rascunho — chamá-lo assim sugeriria
   * que o que a pessoa já enviou se desfez.
   */
  isAdjusting?: boolean;
}

/**
 * O sinal de que o rascunho está no servidor (RNF-P04: confirmação em < 1s).
 *
 * Ele vive **ao lado do título**, e não no canto das ações. O que este texto
 * diz é uma propriedade do documento ("este projeto se salva sozinho"), não um
 * comando disponível — no canto direito, junto de "Descartar rascunho", ele
 * era lido como mais um botão e o olho o descartava com o resto da barra.
 * Encostado no título, ele é legenda: entra no mesmo movimento de leitura em
 * que a pessoa confere o nome do projeto.
 *
 * Daí o itálico dos três estados de repouso (`idle`, `saving`, `saved`): é a
 * marca tipográfica de "isto é uma nota sobre o título, não um segundo
 * título". Os estados de FALHA saem do itálico de propósito — eles pedem ação,
 * e nota de rodapé não pede nada.
 *
 * O estado `conflict` é o único com ação: o rascunho foi alterado em outro
 * aparelho, e a resposta é RECARREGAR, nunca sobrescrever em silêncio
 * (ADR-005). Sobrescrever seria perder o que a pessoa escreveu no celular sem
 * ela nunca saber que existiu.
 */
export function AutosaveIndicator({ state, onReload, isAdjusting }: AutosaveIndicatorProps) {
  if (state === 'conflict') {
    return (
      <div className="flex animate-reveal flex-wrap items-center gap-2 text-sm text-destructive">
        <AlertCircle aria-hidden="true" className="size-4 shrink-0" />
        <span>{messages.draft.conflict}</span>
        <Button type="button" size="sm" variant="outline" onClick={onReload}>
          <RefreshCw aria-hidden="true" className="size-3.5" />
          Recarregar
        </Button>
      </div>
    );
  }

  if (state === 'error') {
    return (
      <p className="flex animate-reveal items-center gap-1.5 text-sm text-destructive" role="status">
        <AlertCircle aria-hidden="true" className="size-4" />
        Não conseguimos salvar agora. Vamos tentar de novo no próximo campo.
      </p>
    );
  }

  return (
    <p
      className="flex items-center gap-1.5 text-xs italic text-muted-foreground"
      role="status"
      aria-live="polite"
    >
      {/* `key={state}`: sem ele o texto trocaria no lugar, sem transição
          nenhuma — o `<span>` é o mesmo nó e o React só mudaria o conteúdo.
          Remontar por estado é o que faz "Salvando…" dar lugar a "Salvo" em
          fade, e é a diferença entre um rótulo que pisca e um que responde. */}
      <span key={state} className="flex animate-fade-in items-center gap-1.5">
        {state === 'saving' ? (
          <>
            <Loader2 aria-hidden="true" className="size-3.5 animate-spin" />
            {messages.draft.saving}
          </>
        ) : state === 'saved' ? (
          <>
            {/* O único `pop` do formulário. RNF-P04 pede confirmação em menos de
                1s; o que confirma de verdade não é o texto, é o gesto do
                visto — a pessoa reconhece pelo canto do olho, sem parar de
                escrever para ler. */}
            <Check aria-hidden="true" className="size-3.5 animate-pop text-emerald-600" />
            {isAdjusting ? messages.draft.savedAdjustments : messages.draft.saved}
          </>
        ) : isAdjusting ? (
          messages.draft.autosaveHintAdjustments
        ) : (
          messages.draft.autosaveHint
        )}
      </span>
    </p>
  );
}
